import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Grid,
  InputAdornment,
  IconButton,
  MenuItem,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";

import {
  fetchDataEngine,
  generateAndExecuteMutation,
  graphqlQuery,
} from "../../../../data/Axios/DynamicService";
import {
  AccountsandFinanceurl,
  companyManagementUrl,
  HRService,
} from "../../../../config";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import Header from "../../../../components/Header";
import { userFinanceDetails } from "../../../../data/Queries/HRmanagement/leavemanagement";
import { allBanks } from "../../../../data/Axios/queries";

// Define validation schema using Yup
const validationSchema = yup.object().shape({
  bankId: yup.string().nullable(),
  branchId: yup.string().nullable(),
  accountNumber: yup.string().nullable(),
  mpesaNumber: yup.string().nullable(),
});

const UserFinanceDetailsForm = (props) => {
  const [data, setData] = useState([]);
  const [banks, setBanks] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedBankId, setSelectedBankId] = useState(null);
  const base_url = HRService.uri;
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const userId = parseInt(decodedToken.Id);
  const [loading, setLoading] = useState(true);

  async function fetchAllData() {
    try {
      const Response = await graphqlQuery(userFinanceDetails, base_url);
      setData(Response);
      const bankData = await graphqlQuery(allBanks, AccountsandFinanceurl.uri);
      if (bankData) {
        setBanks(bankData);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }
  useEffect(() => {
    fetchAllData();
  }, []);
  const fetchBranches = async (bankId) => {
    try {
      const inputFields = { bankId: bankId };
      const returnFields = ["id", "branchName"];
      const response = await fetchDataEngine(
        "allBankBranchesByColumnValues",
        "bankBranchModel",
        inputFields,
        returnFields,
        AccountsandFinanceurl.uri
      );
      const branchOptions = response
        ? response.map((branch) => ({
            value: branch.id,
            label: branch.branchName,
          }))
        : [];

      setBranches(branchOptions);
    } catch (error) {
      console.error("Error fetching branches:", error);
      return [];
    }
  };
  const handleBankChange = (event) => {
    const bankId = event.target.value;
    setSelectedBankId(bankId);
    fetchBranches(bankId || data.bankId);
  };
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const { bankId, branchId, accountNumber, mpesaNumber } = values;
      const mutationData = {
        bankId,
        branchId,
        accountNumber,
        mpesaNumber,
        userId,
      };
      const response = await generateAndExecuteMutation(
        "updateUserFinanceDetails",
       mutationData,
        "financeDetails",
        base_url
      );

      if (Object.values(response)[0] != null) {
        fetchAllData();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Finance details
            updated
        successfully.`,
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request. Please try again later.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const initialValues = {
    bankId: data.bankId || "",
    branchId: data.branchId || "",
    accountNumber: data.accountNumber || "",
    mpesaNumber: data.mpesaNumber || "",
  };

  return (
    <Box m={2}>
      <Box my={2}>
        <Header
          title={"Manage Finance Details"}
          subtitle={"View or update finance details"}
        />
      </Box>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
        }) => (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  select
                  name="bankId"
                  label="Bank"
                  variant="outlined"
                  fullWidth
                  value={values.bankId}
                  onChange={(e) => {
                    handleChange(e);
                    handleBankChange(e);
                  }}
                  onBlur={handleBlur}
                  error={touched.bankId && Boolean(errors.bankId)}
                  helperText={touched.bankId && errors.bankId}
                >
                  {banks.map((bank) => (
                    <MenuItem key={bank.id} value={bank.id}>
                      {bank.bankName}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  name="branchId"
                  label="Branch"
                  variant="outlined"
                  fullWidth
                  value={values.branchId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.branchId && Boolean(errors.branchId)}
                  helperText={touched.branchId && errors.branchId}
                  disabled={!selectedBankId}
                >
                  {branches.map((branch) => (
                    <MenuItem key={branch.value} value={branch.value}>
                      {branch.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="accountNumber"
                  label="Account Number"
                  variant="outlined"
                  fullWidth
                  value={values.accountNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.accountNumber && Boolean(errors.accountNumber)}
                  helperText={touched.accountNumber && errors.accountNumber}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="mpesaNumber"
                  label="Mpesa Number"
                  variant="outlined"
                  fullWidth
                  value={values.mpesaNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.mpesaNumber && Boolean(errors.mpesaNumber)}
                  helperText={touched.mpesaNumber && errors.mpesaNumber}
                />
              </Grid>
              <Grid item xs={6}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default UserFinanceDetailsForm;
